import { Component, OnInit, HostListener } from '@angular/core';
import { MdDialogRef } from '@angular/material';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { plainToClass } from 'class-transformer';
import { Observable } from 'rxjs/Observable';

import { FormErrors } from 'app/shared/validators/generic-validators';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { unsavedFormCheck } from 'app/shared/validators/generic-validators';
import { WarmUpService } from 'app/services/warmup/warmup.service';

import { TaskService } from 'app/task/task.service';
import { Task } from 'app/shared/models/task.model';
import { UserService } from 'app/admin/users/user.service';
import { User } from 'app/shared/models/user.model';

@Component({
  selector: 'addtask',
  templateUrl: './add-task.component.html',
  styleUrls: ['./add-task.component.css']
})

/**
 * Dialog component to update task description through editor.
 */
export class AddTaskComponent implements OnInit {

  addTaskForm: FormGroup;
  users: Array<User>;
  action: string;
  taskData: Task;
  entityId: number;
  assignee: User;
  savingTask: boolean;
  filteredUsers: Observable<User[]>;

  formErrors = {
    'assigneeUserId': '',
    'description': '',
    'title': ''
  };

  validationMessages = {
    'assigneeUserId': {
      'required': 'Required field value.'
    },
    'description': {
      'required': 'Required field value.',
      'maxlength': 'Description is too long.'
    },
    'title': {
      'required': 'Required field value.'
    }
  };

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MdDialogRef<any>,
    private snackBarService: SnackBarService,
    private userService: UserService,
    private taskService: TaskService,
    private trackingService: TrackingService,
    private warmUpService: WarmUpService) { }

  ngOnInit() {
    this.buildForm();
    this.getUserList();
    this.setAssignee();
    this.updateTaskDetailsDataForm();
  }

  buildForm() {
    this.addTaskForm = this.formBuilder.group({
      title: ['', Validators.required],
      assignee: ['', Validators.required],
      description: ['', [Validators.required, Validators.maxLength(4000)]],
      dueDate: [''],
    });
    this.addTaskForm.valueChanges.subscribe(data => this.onValueChanged(data));
  }

  onValueChanged(data?: any) {
    if (!this.addTaskForm) {
      return;
    }
    FormErrors('addTaskForm', this);
  }

  /*
  * Setting assigneeName form control can use a separate function because assignee
  * can be the displayName or (if displayName is null) the loginId
  */
  setAssignee(): void {
    this.filteredUsers = this.addTaskForm.get('assignee').valueChanges
      .startWith(null)
      .map(user => user && typeof user === 'object' ? user.name : user)
      .map(name => name ? this.filterUsers(name) : (this.users ? this.users.slice() : this.users));
  }

  filterUsers(val: string): User[] {
    return val ? this.users.filter(s => (s.name.toLowerCase().indexOf(val.toLowerCase()) === 0)) : this.users;
  }

  updateTaskDetailsDataForm(): void {
    if (this.taskData) {
      this.addTaskForm.patchValue({
        description: this.taskData['description'],
        title: this.taskData['title'],
        dueDate: this.taskData['dueDate'] ? new Date(this.taskData['dueDate']) : '',
      });
    }
  }

  getUserList() {
    this.users = this.warmUpService.getUsersList();
    if (this.taskData && this.users) {
      this.assignee = this.users.find(user => user.loginId === this.taskData.assignee);
      this.addTaskForm.get('assignee').setValue(this.assignee);
    }
  }

  onSubmit({ value, valid }): void {
    this.savingTask = true;
    if (this.action === 'Add') {
      const task = new Task();
      task.title = value.title;
      task.description = value.description;
      task.dueDate = value.dueDate;
      task.assignee = value.assignee.loginId;
      task.entityId = this.entityId;
      this.taskService.addTask(task)
        .subscribe(
        success => {
          this.savingTask = false;
          if (success.flagResult) {
            this.dialogRef.close(success.arrResult);
          }
          this.trackingService
            .emitEvent(
            TrackingConstants.TASK_MANAGEMENT,
            TrackingConstants.PERFORM_ADD_TASK,
            TrackingConstants.SUCCESS, 1);
          this.callSnackBarService(success, this.action);
        },
        err => {
          this.savingTask = false;
          this.trackingService
            .emitEvent(
            TrackingConstants.TASK_MANAGEMENT,
            TrackingConstants.PERFORM_ADD_TASK,
            TrackingConstants.FAILURE, 0);
          this.callSnackBarService(err, this.action);
        }
        );
    } else {
      this.taskData.title = value.title;
      this.taskData.description = value.description;
      this.taskData.dueDate = value.dueDate;
      this.taskData.assignee = value.assignee.loginId;

      // Copy task to submit mutation, to prevent errors
      const mutateTask = Object.assign({}, this.taskData);

      delete mutateTask['adhocId'];
      delete mutateTask['comment'];
      delete mutateTask['newComment'];
      delete mutateTask['addFirstComment'];

      this.taskService.updateTask(mutateTask)
        .subscribe(
        success => {
          this.savingTask = false;
          if (success.flagResult) {
            this.dialogRef.close(success.arrResult);
          }
          this.trackingService
            .emitEvent(
            TrackingConstants.TASK_MANAGEMENT,
            TrackingConstants.PERFORM_UPDATE_TASK,
            TrackingConstants.SUCCESS, 1);
          this.callSnackBarService(success, this.action);
        },
        err => {
          this.savingTask = false;
          this.trackingService
            .emitEvent(
            TrackingConstants.TASK_MANAGEMENT,
            TrackingConstants.PERFORM_UPDATE_TASK,
            TrackingConstants.FAILURE, 0);
          this.callSnackBarService(err, this.action);
        }
        );
    }
  }

  callSnackBarService(response: any, operation: string) {
    this.snackBarService.showStatus(response, operation === 'Add'
      && MessageService.TASK_ADDED_SUCCESS || MessageService.TASK_UPDATED_SUCCESS,
      MessageService.TASK_UPDATED_FAILURE);
  }

  displayFn(user: User): string {
    return user ? user.name : '';
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    unsavedFormCheck(event, this.dialogRef, this.addTaskForm);
  }
}



// WEBPACK FOOTER //
// ./src/app/task/addtask/add-task.component.ts